import { useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { AuthLayout } from "@/components/auth-layout"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { authClient } from "@/lib/auth-client"
import { isOAuthPage } from "@/lib/oauth"
import { refreshSession } from "@/lib/queries"
import { signInRoute } from "@/router"

export function SignInPage() {
  const navigate = useNavigate()
  const { redirect } = signInRoute.useSearch()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const done = async () => {
    await refreshSession(queryClient)
    await (redirect
      ? navigate({ href: redirect, reloadDocument: isOAuthPage(redirect) })
      : navigate({ to: "/" }))
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setPending(true)
    setError(null)
    const { error } = await authClient.signIn.email({
      email: String(form.get("email")),
      password: String(form.get("password")),
    })
    setPending(false)
    if (error) return setError(error.message ?? "Sign in failed")
    await done()
  }

  const onPasskey = async () => {
    setError(null)
    const result = await authClient.signIn.passkey()
    if (result?.error) return setError(result.error.message ?? "Passkey sign in failed")
    await done()
  }

  return (
    <AuthLayout
      title="Sign in"
      description="Welcome back to Minga."
      footer={
        <>
          No account yet?{" "}
          <Link to="/sign-up" search={redirect ? { redirect } : {}} className="font-medium link">
            Create one
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={onSubmit}>
        <Field id="email" label="Email" type="email" autoComplete="username webauthn" required />
        <div className="grid gap-1.5">
          <Field id="password" label="Password" type="password" autoComplete="current-password" required />
          <Link
            to="/forgot-password"
            search={redirect ? { redirect } : {}}
            className="justify-self-end text-sm link"
          >
            Forgot password?
          </Link>
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        <Button type="submit" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted">
        <span className="h-px flex-1 bg-line" />
        or
        <span className="h-px flex-1 bg-line" />
      </div>
      <Button variant="secondary" className="w-full" onClick={onPasskey}>
        Sign in with passkey
      </Button>
    </AuthLayout>
  )
}
